import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePendingReviewWithdrawals } from '../../hooks/usePendingReviewWithdrawals';
import { useWithdrawalsMutations } from '../../hooks/useWithdrawalsMutations';
import type { Withdrawal } from '../../models';
import {
  canSelectWithdrawal,
  computeInitialPaymentFileState,
  computePaymentFileAmounts,
  type WithdrawalPaymentAdjustment,
} from '../../utils/generatePaymentFilesBalance';

export interface GeneratePaymentFilesModalRef {
  open: () => void;
  close: () => void;
}

interface GeneratePaymentFilesModalProps {
  onSuccess?: () => void;
}

const TABLE_GRID_CLASS = 'grid grid-cols-[1rem_minmax(0,1.8fr)_minmax(0,1.4fr)_7rem_7rem_8rem] items-start gap-x-6';

export const GeneratePaymentFilesModal = forwardRef<GeneratePaymentFilesModalRef, GeneratePaymentFilesModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('withdrawalsModule');
    const { toast } = useToast();
    const navigate = useNavigate();
    const currency = useCurrency();
    const modalRef = useRef<ModalCenterRef>(null);
    const balanceAlertShownRef = useRef(false);
    const { generatePaymentFiles } = useWithdrawalsMutations();

    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [approvedAmounts, setApprovedAmounts] = useState<Record<number, number>>({});
    const [adjustments, setAdjustments] = useState<Map<number, WithdrawalPaymentAdjustment>>(new Map());

    const { data: pendingWithdrawals = [], isLoading, isError, refetch } = usePendingReviewWithdrawals(isOpen);

    const applySelection = useCallback(
      (ids: Set<number>) => {
        const { approvedAmounts: nextAmounts, adjustments: nextAdjustments } = computePaymentFileAmounts(
          pendingWithdrawals,
          ids,
        );
        setSelectedIds(ids);
        setApprovedAmounts(nextAmounts);
        setAdjustments(nextAdjustments);
        return nextAdjustments;
      },
      [pendingWithdrawals],
    );

    useEffect(() => {
      if (!isOpen || pendingWithdrawals.length === 0) return;

      const {
        selectedIds: initialSelectedIds,
        approvedAmounts: initialAmounts,
        adjustments: initialAdjustments,
        excludedCount,
      } = computeInitialPaymentFileState(pendingWithdrawals);

      setSelectedIds(initialSelectedIds);
      setApprovedAmounts(initialAmounts);
      setAdjustments(initialAdjustments);

      if (excludedCount > 0 && !balanceAlertShownRef.current) {
        balanceAlertShownRef.current = true;
        toast({
          title: t('modals.generatePaymentFiles.excludedByBalance.title'),
          description: t('modals.generatePaymentFiles.excludedByBalance.description', { count: excludedCount }),
          variant: 'destructive',
        });
      }
    }, [isOpen, pendingWithdrawals, t, toast]);

    useImperativeHandle(ref, () => ({
      open: () => {
        balanceAlertShownRef.current = false;
        setIsOpen(true);
        modalRef.current?.openModal();
      },
      close: () => {
        setIsOpen(false);
        modalRef.current?.closeModal();
      },
    }));

    const excludedByBalanceCount = useMemo(
      () =>
        pendingWithdrawals.filter(
          (w) => !selectedIds.has(w.id) && !canSelectWithdrawal(pendingWithdrawals, selectedIds, w.id),
        ).length,
      [pendingWithdrawals, selectedIds],
    );

    const excludedIds = useMemo(
      () => pendingWithdrawals.filter((w) => !selectedIds.has(w.id)).map((w) => w.id),
      [pendingWithdrawals, selectedIds],
    );

    const toggleWithdrawal = useCallback(
      (id: number, checked: boolean) => {
        if (!checked) {
          const next = new Set(selectedIds);
          next.delete(id);
          applySelection(next);
          return;
        }

        if (!canSelectWithdrawal(pendingWithdrawals, selectedIds, id)) {
          toast({
            title: t('modals.generatePaymentFiles.excludedByBalance.title'),
            description: t('modals.generatePaymentFiles.excludedByBalance.selectBlocked'),
            variant: 'destructive',
          });
          return;
        }

        const next = new Set(selectedIds);
        next.add(id);
        applySelection(next);
      },
      [applySelection, pendingWithdrawals, selectedIds, t, toast],
    );

    const toggleAll = useCallback(
      (checked: boolean) => {
        if (!checked) {
          applySelection(new Set());
          return;
        }

        const { selectedIds: eligibleIds } = computeInitialPaymentFileState(pendingWithdrawals);
        applySelection(eligibleIds);
      },
      [applySelection, pendingWithdrawals],
    );

    const updateApprovedAmount = useCallback((id: number, value: string) => {
      const parsed = Number(value);
      if (value === '' || Number.isNaN(parsed)) return;
      setApprovedAmounts((prev) => ({ ...prev, [id]: parsed }));
    }, []);

    const selectedApprovedAmounts = useMemo(() => {
      const amounts: Record<string, number> = {};
      pendingWithdrawals.forEach((w) => {
        if (!selectedIds.has(w.id)) return;
        amounts[String(w.id)] = approvedAmounts[w.id] ?? w.approved_amount ?? w.requested_amount;
      });
      return amounts;
    }, [pendingWithdrawals, selectedIds, approvedAmounts]);

    const handleClose = useCallback(() => {
      setIsOpen(false);
      modalRef.current?.closeModal();
    }, []);

    const handleGenerate = async () => {
      try {
        const payload = {
          excluded_ids: excludedIds,
          approved_amounts: selectedApprovedAmounts,
        };

        const response = await generatePaymentFiles.mutateAsync(payload);

        toast({
          title: t('modals.generatePaymentFiles.success.title'),
          description:
            response.data?.generation_id != null
              ? t('modals.generatePaymentFiles.success.descriptionWithId', {
                  generationId: response.data.generation_id,
                })
              : t('modals.generatePaymentFiles.success.description'),
        });

        handleClose();
        onSuccess?.();
        navigate('/wallets/withdrawals');
      } catch (error) {
        toast({
          title: t('modals.generatePaymentFiles.error.title'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isGenerating = generatePaymentFiles.isPending;
    const selectableCount = useMemo(
      () => pendingWithdrawals.filter((w) => canSelectWithdrawal(pendingWithdrawals, selectedIds, w.id)).length,
      [pendingWithdrawals, selectedIds],
    );
    const allSelected = selectableCount > 0 && selectedIds.size === selectableCount;

    const formatAmount = (value?: number | null, currencyCode = currency) => {
      if (value === undefined || value === null) return '—';
      return formatCurrencyAmount(value, currencyCode);
    };

    const renderWithdrawalRow = (withdrawal: Withdrawal) => {
      const adjustment = adjustments.get(withdrawal.id);
      const bankAccount = withdrawal.bank_account;
      const withdrawalCurrency = withdrawal.wallet?.currency ?? currency;
      const clientName =
        withdrawal.wallet?.team?.name ?? t('modals.generatePaymentFiles.walletLabel', { id: withdrawal.wallet_id });
      const walletAccount = withdrawal.wallet?.description?.trim();
      const isSelected = selectedIds.has(withdrawal.id);
      const isBlocked = !isSelected && !canSelectWithdrawal(pendingWithdrawals, selectedIds, withdrawal.id);
      const hasInsufficientBalance = isBlocked || adjustment?.reason === 'insufficientBalance';
      const hasPartialApproval = isSelected && adjustment?.reason === 'partialApproval';

      return (
        <div
          key={withdrawal.id}
          className={`rounded-lg border px-4 py-3 ${
            hasInsufficientBalance ? 'border-error/50 bg-bg-error/30' : 'border-lines'
          }`}
          data-testid={`generate-payment-file-withdrawal-${withdrawal.id}`}
        >
          <div className={TABLE_GRID_CLASS}>
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => toggleWithdrawal(withdrawal.id, checked === true)}
              disabled={isGenerating || hasInsufficientBalance}
              className="mt-0.5 shrink-0"
              data-testid={`generate-payment-file-checkbox-${withdrawal.id}`}
            />

            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium text-title truncate">{clientName}</p>
              {walletAccount && <p className="text-xs text-complementary-text truncate">{walletAccount}</p>}
            </div>

            <div className="min-w-0 space-y-1">
              {bankAccount ? (
                <>
                  <p className="text-sm font-mono text-subtext truncate" title={bankAccount.iban}>
                    {bankAccount.iban}
                  </p>
                  {bankAccount.bank_name && (
                    <p className="text-xs text-complementary-text truncate">{bankAccount.bank_name}</p>
                  )}
                  {bankAccount.bic && (
                    <p className="text-xs text-complementary-text truncate">BIC: {bankAccount.bic}</p>
                  )}
                </>
              ) : (
                <span className="text-sm text-subtext">—</span>
              )}
            </div>

            <p className="self-center text-sm tabular-nums text-foreground text-right">
              {formatAmount(withdrawal.requested_amount, withdrawalCurrency)}
            </p>

            <p className="self-center text-sm tabular-nums text-foreground text-right">
              {formatAmount(adjustment?.walletBalance, withdrawalCurrency)}
            </p>

            <div className="self-center">
              {isSelected ? (
                <Input
                  id={`approved-amount-${withdrawal.id}`}
                  type="number"
                  min={0}
                  step="0.01"
                  className="h-9 text-sm tabular-nums"
                  value={approvedAmounts[withdrawal.id] ?? ''}
                  onChange={(e) => updateApprovedAmount(withdrawal.id, e.target.value)}
                  disabled={isGenerating}
                  aria-label={t('modals.generatePaymentFiles.approvedAmount')}
                  data-testid={`generate-payment-file-approved-amount-${withdrawal.id}`}
                />
              ) : (
                <span className="block text-right text-sm text-subtext">—</span>
              )}
            </div>
          </div>

          {hasInsufficientBalance && (
            <div className={`mt-3 ${TABLE_GRID_CLASS}`}>
              <span aria-hidden="true" />
              <p className="col-span-5 flex items-center gap-2.5 text-xs text-error">
                <FontAwesomeIcon icon={faExclamationTriangle} className="h-3.5 w-3.5 shrink-0" />
                <span>{t('modals.generatePaymentFiles.excludedByBalance.row')}</span>
              </p>
            </div>
          )}

          {hasPartialApproval && (
            <div className={`mt-3 ${TABLE_GRID_CLASS}`}>
              <span aria-hidden="true" />
              <p className="col-span-5 flex items-center gap-2.5 text-xs text-warning">
                <FontAwesomeIcon icon={faExclamationTriangle} className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {t('modals.generatePaymentFiles.partialApproval.row', {
                    balance: formatAmount(adjustment?.approvedAmount, withdrawalCurrency),
                  })}
                </span>
              </p>
            </div>
          )}
        </div>
      );
    };

    return (
      <ModalCenter ref={modalRef} title={t('modals.generatePaymentFiles.title')} width="xxl">
        <div className="space-y-6">
          <p className="text-sm text-subtext">{t('modals.generatePaymentFiles.description')}</p>

          {isLoading && <p className="text-sm text-subtext">{t('modals.generatePaymentFiles.loading')}</p>}

          {isError && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-error bg-bg-error p-3">
              <p className="text-sm text-error">{t('modals.generatePaymentFiles.loadError')}</p>
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="retry-pending-withdrawals">
                {t('buttonsShort.retry')}
              </Button>
            </div>
          )}

          {!isLoading && !isError && pendingWithdrawals.length === 0 && (
            <p className="text-sm text-subtext">{t('modals.generatePaymentFiles.empty')}</p>
          )}

          {!isLoading && pendingWithdrawals.length > 0 && (
            <>
              {excludedByBalanceCount > 0 && (
                <Alert variant="destructive" className="py-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    {t('modals.generatePaymentFiles.excludedByBalance.description', { count: excludedByBalanceCount })}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-between gap-4 px-1">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(checked) => toggleAll(checked === true)}
                    disabled={isGenerating || selectableCount === 0}
                    data-testid="generate-payment-file-select-all"
                  />
                  <span className="text-sm font-medium">
                    {t('modals.generatePaymentFiles.selectAll', { count: pendingWithdrawals.length })}
                  </span>
                </label>
                <Badge variant="outline">
                  {t('modals.generatePaymentFiles.selectedCount', { count: selectedIds.size })}
                </Badge>
              </div>

              <div className="space-y-3">
                <div className={`${TABLE_GRID_CLASS} px-4 pb-2 text-xs font-medium text-subtext`}>
                  <span aria-hidden="true" />
                  <span>{t('table.columns.client')}</span>
                  <span>{t('table.columns.bank_account')}</span>
                  <span className="text-right">{t('modals.generatePaymentFiles.requestedAmount')}</span>
                  <span className="text-right">{t('modals.generatePaymentFiles.walletBalance')}</span>
                  <span>{t('modals.generatePaymentFiles.approvedAmount')}</span>
                </div>

                <div className="max-h-[28rem] overflow-y-auto space-y-3 pr-1">
                  {pendingWithdrawals.map(renderWithdrawalRow)}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isGenerating}
              data-testid="cancel-generate-payment-files"
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={isGenerating || isLoading || pendingWithdrawals.length === 0 || selectedIds.size === 0}
              data-testid="confirm-generate-payment-files"
            >
              {isGenerating ? t('buttons.generatingPaymentFiles') : t('modals.generatePaymentFiles.confirm')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

GeneratePaymentFilesModal.displayName = 'GeneratePaymentFilesModal';
